/**
 * Shipments API — mirrors `cargoShipments.js` routes 1:1.
 * Returns the response `data` field unwrapped; callers get plain objects.
 */
import { api } from './client';
import type { Shipment } from '@/types/shipment';

export interface CreateShipmentInput {
  receiverName: string;
  receiverPhone: string;
  packageDescription: string;
  weight: number;
  price?: number;
  paymentMethod?: 'cash' | 'wallet';
  paidBy?: 'sender' | 'receiver';
  currency?: string;

  receiverCity?: string;
  receiverCountry?: string;
  receiverAddress?: string;
  receiverLat?: number;
  receiverLng?: number;

  pickupAddress?: string;
  pickupCity?: string;
  pickupCountry?: string;
  pickupLat?: number;
  pickupLng?: number;
  pickupLandmark?: string;
  deliveryLandmark?: string;
  pickupMethod?: 'door' | 'center';
  deliveryMethod?: 'door' | 'center';
  pickupCenterId?: number;
  deliveryCenterId?: number;

  packageType?: string;
  catalogItemKey?: string;
  contentKey?: string;
  speedKey?: string;
  packaging?: string;
  insurance?: boolean;
  cod?: boolean;
  signature?: boolean;

  dimensions?: { length: number; width: number; height: number };
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  quantity?: number;
  estimatedValue?: number;
  isFragile?: boolean;
  requiresRefrigeration?: boolean;
  notes?: string;
  images?: string[];

  tripId?: number;
  waitForOffers?: boolean;
  pricingSource?: 'engine' | 'driver' | 'manual';
}

function unwrap<T>(res: { data?: any }): T {
  return (res.data?.data ?? res.data) as T;
}

export const shipmentsApi = {
  /** Auth: list the caller's shipments by role. */
  async listMy(role: 'sender' | 'receiver' | 'driver' = 'sender'): Promise<Shipment[]> {
    const r = await api.get(`/api/cargo/shipments/my`, { params: { role } });
    return unwrap<Shipment[]>(r);
  },

  async listPending(): Promise<Shipment[]> {
    const r = await api.get(`/api/cargo/shipments/pending`);
    return unwrap<Shipment[]>(r);
  },

  async findById(id: number): Promise<Shipment> {
    const r = await api.get(`/api/cargo/shipments/${id}`);
    return unwrap<Shipment>(r);
  },

  /** Public: track by tracking number, no auth required. */
  async track(trackingNumber: string): Promise<Shipment> {
    const r = await api.get(`/api/cargo/shipments/track/${encodeURIComponent(trackingNumber)}`);
    return unwrap<Shipment>(r);
  },

  /** Auth: short-code lookup (visible only to parties involved). */
  async lookup(shortCode: string): Promise<Shipment> {
    const r = await api.get(`/api/cargo/shipments/lookup/${encodeURIComponent(shortCode)}`);
    return unwrap<Shipment>(r);
  },

  async create(input: CreateShipmentInput): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments`, input);
    return unwrap<Shipment>(r);
  },

  async cancel(id: number, reason?: string): Promise<void> {
    await api.post(`/api/cargo/shipments/${id}/cancel`, { reason });
  },

  async approveClaim(id: number): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/approve-claim`);
    return unwrap<Shipment>(r);
  },

  async rejectClaim(id: number, reason?: string): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/reject-claim`, { reason });
    return unwrap<Shipment>(r);
  },

  async updatePaymentMethod(id: number, paymentMethod: 'cash' | 'wallet', paidBy?: 'sender' | 'receiver'): Promise<Shipment> {
    const r = await api.put(`/api/cargo/shipments/${id}/payment-method`, { paymentMethod, paidBy });
    return unwrap<Shipment>(r);
  },

  async reportIllegal(id: number, reason: string, details?: string): Promise<void> {
    await api.post(`/api/cargo/shipments/${id}/report-illegal`, { reason, details });
  },

  async findDrivers(id: number): Promise<unknown[]> {
    const r = await api.get(`/api/cargo/shipments/${id}/find-drivers`);
    return unwrap<unknown[]>(r);
  },

  async requestDriver(id: number, driverId: string, tripId?: number): Promise<void> {
    await api.post(`/api/cargo/shipments/${id}/request-driver`, { driverId, tripId });
  },

  async cancelDirectRequest(id: number): Promise<void> {
    await api.post(`/api/cargo/shipments/${id}/cancel-direct-request`);
  },

  // ── Driver-side actions ──────────────────────────────────────────
  async listPendingForDrivers(): Promise<Shipment[]> {
    const r = await api.get(`/api/cargo/shipments/pending`);
    return unwrap<Shipment[]>(r);
  },
  async accept(id: number): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/accept`);
    return unwrap<Shipment>(r);
  },
  async claim(id: number, opts: { tripId?: number; offeredPrice?: number }): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/claim`, opts);
    return unwrap<Shipment>(r);
  },
  async rejectByDriver(id: number, reason?: string): Promise<void> {
    await api.post(`/api/cargo/shipments/${id}/reject`, { reason });
  },
  async generatePickupOtp(id: number): Promise<{ sent: boolean }> {
    const r = await api.post(`/api/cargo/shipments/${id}/pickup-otp`);
    return unwrap<{ sent: boolean }>(r);
  },
  async confirmPickup(id: number, opts: {
    photoUrl?: string; otpCode?: string; shortCode?: string;
    pieceCount?: number; pickupLat?: number; pickupLng?: number;
  }): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/confirm-pickup`, opts);
    return unwrap<Shipment>(r);
  },
  async generateDeliveryOtp(id: number): Promise<{ sent: boolean }> {
    const r = await api.post(`/api/cargo/shipments/${id}/delivery-otp`);
    return unwrap<{ sent: boolean }>(r);
  },
  async confirmDelivery(id: number, opts: {
    photoUrl?: string; otpCode?: string; signatureUrl?: string;
  }): Promise<Shipment> {
    const r = await api.post(`/api/cargo/shipments/${id}/confirm-delivery`, opts);
    return unwrap<Shipment>(r);
  },
};
